import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Box, IconButton, Typography, CircularProgress, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

// Import services
import { getDocumentViewUrl } from './services/closeoutDocsService';

// Import viewer components (to be created)
import PDFViewer from './components/DocumentViewers/PDFViewer';
import ImageViewer from './components/DocumentViewers/ImageViewer';
import TextViewer from './components/DocumentViewers/TextViewer';
import UnsupportedViewer from './components/DocumentViewers/UnsupportedViewer';

/**
 * Determine viewer type based on file extension
 * @param {string} extension - File extension (e.g., 'pdf', 'jpg')
 * @returns {string} - 'pdf', 'image', 'text', or 'unsupported'
 */
const getViewerType = (extension) => {
    const ext = extension?.toLowerCase();

    // PDF files
    if (ext === 'pdf') return 'pdf';

    // Image files
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(ext)) return 'image';

    // Text files
    if (['txt', 'log', 'md', 'json', 'xml', 'csv'].includes(ext)) return 'text';

    // Everything else is unsupported for inline viewing
    return 'unsupported';
};

/**
 * DocumentViewerPage Component
 * Full-page document viewer with Google Drive-style UI
 *
 * @returns {React.ReactElement} Document viewer page
 */
const DocumentViewerPage = () => {
    const theme = useTheme();
    const { projectId, documentId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    // Get document metadata from navigation state (if available)
    const documentFromState = location.state?.document;

    // State
    const [document, setDocument] = useState(documentFromState || null);
    const [loading, setLoading] = useState(!documentFromState);
    const [viewerType, setViewerType] = useState('pdf');
    const [documentUrl, setDocumentUrl] = useState(null);

    /**
     * Load document metadata
     */
    useEffect(() => {
        if (documentFromState) {
            setDocument(documentFromState);
            setLoading(false);
        } else {
            // No document data - this shouldn't happen in normal flow
            // User should navigate here from the document list
            setLoading(false);
        }
    }, [documentFromState]);

    /**
     * Set up document URL and viewer type when document is available
     */
    useEffect(() => {
        if (document && documentId && projectId) {
            const url = getDocumentViewUrl(documentId, projectId);
            setDocumentUrl(url);
            setViewerType(getViewerType(document.fileExtension));
        }
    }, [document, documentId, projectId]);

    /**
     * Handle back navigation
     */
    const handleBack = useCallback(() => {
        // Navigate back to closeout docs, preserving folder context if available
        const folderId = location.state?.folderId;
        if (folderId) {
            navigate(`/projects/${projectId}/closeout-docs?folderId=${folderId}`);
        } else {
            navigate(`/projects/${projectId}/closeout-docs`);
        }
    }, [navigate, projectId, location.state]);

    /**
     * Render appropriate viewer based on file type
     */
    const renderViewer = () => {
        if (!documentUrl) return null;

        const viewerProps = {
            documentUrl,
            filename: document?.originalFilename || 'Document',
            extension: document?.fileExtension,
        };

        switch (viewerType) {
            case 'pdf':
                return <PDFViewer {...viewerProps} />;
            case 'image':
                return <ImageViewer {...viewerProps} />;
            case 'text':
                return <TextViewer {...viewerProps} />;
            case 'unsupported':
                return <UnsupportedViewer {...viewerProps} document={document} />;
            default:
                return <UnsupportedViewer {...viewerProps} document={document} />;
        }
    };

    // Loading state
    if (loading) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '100vh',
                    backgroundColor: theme.palette.grey[50],
                }}
            >
                <CircularProgress size={60} />
                <Typography variant="body1" sx={{ mt: 2 }} color="text.secondary">
                    Loading document...
                </Typography>
            </Box>
        );
    }

    // Main viewer UI
    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100vh',
                backgroundColor: theme.palette.grey[50],
                overflow: 'hidden',
            }}
        >
            {/* Header Bar - Ultra Minimal (Google Drive Style) */}
            <Box
                sx={{
                    backgroundColor: theme.palette.background.paper,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    px: { xs: 1.5, sm: 2, md: 3 },
                    py: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    flexShrink: 0,
                    boxShadow: theme.shadows[1],
                    minHeight: 64,
                }}
            >
                {/* Back button + Filename */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0, flex: 1 }}>
                    <Tooltip title="Back to documents">
                        <IconButton
                            onClick={handleBack}
                            sx={{
                                color: theme.palette.text.primary,
                                flexShrink: 0,
                            }}
                            aria-label="Go back"
                        >
                            <ArrowBackIcon />
                        </IconButton>
                    </Tooltip>
                    <Typography
                        variant="h6"
                        sx={{
                            fontWeight: 500,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            fontSize: { xs: '1rem', sm: '1.25rem' },
                            color: theme.palette.text.primary,
                        }}
                        title={document?.originalFilename}
                    >
                        {document?.originalFilename || 'Document'}
                    </Typography>
                </Box>
            </Box>

            {/* Document Viewer Area - Takes full remaining height */}
            <Box
                sx={{
                    flex: 1,
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                    minHeight: 0, // Important for flex children with overflow
                }}
            >
                {renderViewer()}
            </Box>
        </Box>
    );
};

export default DocumentViewerPage;
